import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Film,
  Image as ImageIcon,
  Layers,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import apiClient from '../../services/api';
import Input from '../ui/Input';

// Seletor de publicação do gatilho "comentário no Instagram/Facebook".
// Lista as publicações recentes da conta conectada (GET /social/posts/recent) e
// grava o MESMO id que o webhook de comentário manda (IG: id da mídia; FB:
// "{pagina}_{post}"), então fluxos antigos com ID colado à mão seguem valendo.
// Vazio = todas as publicações. O campo de ID manual continua como alternativa.

export type SocialPostPlatform = 'instagram' | 'facebook';

export interface SocialPickerPost {
  id: string;
  caption: string;
  media_type: 'image' | 'video' | 'reel' | 'carousel' | 'text';
  thumbnail_url: string | null;
  permalink: string | null;
  timestamp: string | null;
}

interface RecentPostsPayload {
  connected: boolean;
  account: { username: string | null } | null;
  posts: SocialPickerPost[];
  next_cursor: string | null;
  error?: string;
}

type ErrorCode = 'not_connected' | 'token_invalid' | 'permission_denied' | 'rate_limited' | 'account_incomplete' | 'graph_error';

interface SocialPostPickerProps {
  platform: SocialPostPlatform;
  value: string;
  onChange: (postId: string) => void;
}

const NETWORK_NAME: Record<SocialPostPlatform, string> = { instagram: 'Instagram', facebook: 'Facebook' };
const ACCENT: Record<SocialPostPlatform, { row: string; check: string }> = {
  instagram: { row: 'bg-pink-50 dark:bg-pink-900/20', check: 'text-pink-600' },
  facebook: { row: 'bg-blue-50 dark:bg-blue-900/20', check: 'text-blue-600' },
};

// Primeira página fica 2 min em memória: trocar de nó e voltar não refaz a busca.
const CACHE_TTL_MS = 2 * 60 * 1000;
const firstPageCache = new Map<string, { at: number; data: RecentPostsPayload }>();
const cacheKey = (platform: SocialPostPlatform) => {
  let project = '';
  try { project = localStorage.getItem('currentProjectId') || ''; } catch { /* sem storage */ }
  return `${project}:${platform}`;
};

const PostThumb: React.FC<{ post: SocialPickerPost; size?: 'sm' | 'md' }> = ({ post, size = 'md' }) => {
  const [broken, setBroken] = useState(false);
  const box = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  const Icon = post.media_type === 'text' ? FileText : post.media_type === 'carousel' ? Layers : post.media_type === 'image' ? ImageIcon : Film;
  return (
    <div className={`${box} shrink-0 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center`}>
      {post.thumbnail_url && !broken ? (
        <img
          src={post.thumbnail_url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <Icon className="w-5 h-5 text-gray-400" />
      )}
    </div>
  );
};

const SocialPostPicker: React.FC<SocialPostPickerProps> = ({ platform, value, onChange }) => {
  const { t, i18n } = useTranslation();
  const network = NETWORK_NAME[platform];
  const accent = ACCENT[platform];

  const [posts, setPosts] = useState<SocialPickerPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);

  // Só usa setters (estáveis), então não muda entre renders.
  const applyPayload = useCallback((data: RecentPostsPayload) => {
    setPosts(data.posts || []);
    setNextCursor(data.next_cursor || null);
    setUsername(data.account?.username || null);
    if (!data.connected) setErrorCode('not_connected');
    else if (data.error) setErrorCode((data.error as ErrorCode) || 'graph_error');
    else setErrorCode(null);
  }, []);

  const load = useCallback(async (force = false) => {
    const key = cacheKey(platform);
    const cached = firstPageCache.get(key);
    if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
      applyPayload(cached.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorCode(null);
    try {
      const res = await apiClient.get('/social/posts/recent', { params: { platform, limit: 24 } });
      const data: RecentPostsPayload = res.data?.data || { connected: false, account: null, posts: [], next_cursor: null };
      applyPayload(data);
      if (data.connected && !data.error) firstPageCache.set(key, { at: Date.now(), data });
    } catch (err: any) {
      setPosts([]);
      setNextCursor(null);
      setErrorCode((err?.response?.data?.code as ErrorCode) || 'graph_error');
    } finally {
      setLoading(false);
    }
  }, [platform, applyPayload]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await apiClient.get('/social/posts/recent', { params: { platform, limit: 24, after: nextCursor } });
      const data: RecentPostsPayload | undefined = res.data?.data;
      const more = data?.posts || [];
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...more.filter((p) => !seen.has(p.id))];
      });
      setNextCursor(data?.next_cursor || null);
    } catch {
      // Mantém o que já carregou; o botão continua lá para tentar de novo.
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(i18n.language || 'pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return d.toLocaleDateString();
    }
  };

  const typeLabel = (type: SocialPickerPost['media_type']) => {
    switch (type) {
      case 'reel': return t('workflow.social.postPicker.typeReel', 'Reels');
      case 'video': return t('workflow.social.postPicker.typeVideo', 'Vídeo');
      case 'carousel': return t('workflow.social.postPicker.typeCarousel', 'Carrossel');
      case 'text': return t('workflow.social.postPicker.typeText', 'Texto');
      default: return '';
    }
  };

  const selectedPost = value ? posts.find((p) => p.id === value) || null : null;
  const listAvailable = !loading && !errorCode;
  // Sem lista (erro / sem conta) o campo manual fica sempre à mostra.
  const showManual = manual || (!loading && !!errorCode);

  const choose = (postId: string) => {
    onChange(postId);
    setOpen(false);
  };

  const errorMessage = () => {
    switch (errorCode) {
      case 'not_connected':
        return t('workflow.social.postPicker.notConnected', {
          network,
          defaultValue: `Nenhuma conta do ${network} conectada neste projeto. Conecte a conta para escolher a publicação aqui ou cole o ID abaixo.`,
        });
      case 'token_invalid':
        return t('workflow.social.postPicker.tokenInvalid', {
          network,
          defaultValue: `A conexão com o ${network} expirou. Reconecte a conta para ver as publicações ou cole o ID abaixo.`,
        });
      case 'permission_denied':
        return t('workflow.social.postPicker.permissionDenied', {
          network,
          defaultValue: `A conta do ${network} não deu permissão para listar as publicações. Reconecte a conta ou cole o ID abaixo.`,
        });
      default:
        return t('workflow.social.postPicker.loadError', 'Não foi possível carregar as publicações agora. Tente de novo ou cole o ID abaixo.');
    }
  };

  const renderSummary = () => {
    if (!value) {
      return (
        <>
          <div className="w-10 h-10 shrink-0 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <Layers className="w-5 h-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.postPicker.allPosts', 'Todas as publicações')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('workflow.social.postPicker.allPostsHint', 'Dispara em comentários de qualquer publicação')}</p>
          </div>
        </>
      );
    }
    if (selectedPost) {
      return (
        <>
          <PostThumb post={selectedPost} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-900 dark:text-white truncate">
              {selectedPost.caption || t('workflow.social.postPicker.noCaption', '(sem legenda)')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {[formatDate(selectedPost.timestamp), typeLabel(selectedPost.media_type)].filter(Boolean).join(' · ')}
            </p>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="w-10 h-10 shrink-0 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <FileText className="w-5 h-5 text-gray-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-900 dark:text-white truncate">
            {t('workflow.social.postPicker.byId', { id: value, defaultValue: `Publicação com ID ${value}` })}
          </p>
          {listAvailable && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('workflow.social.postPicker.notInRecent', 'Não está entre as publicações recentes')}</p>
          )}
        </div>
      </>
    );
  };

  return (
    <div>
      {/* Seleção atual — abre/fecha a lista */}
      <button
        type="button"
        onClick={() => listAvailable && setOpen((o) => !o)}
        disabled={!listAvailable}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-left hover:border-gray-400 dark:hover:border-gray-500 disabled:cursor-default disabled:hover:border-gray-300 dark:disabled:hover:border-gray-600 transition-colors"
      >
        {loading ? (
          <span className="flex items-center gap-2 py-2.5 px-1 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('workflow.social.postPicker.loading', 'Carregando publicações...')}
          </span>
        ) : (
          <>
            {renderSummary()}
            {listAvailable && (open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />)}
          </>
        )}
      </button>

      {selectedPost?.permalink && !open && (
        <a
          href={selectedPost.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
        >
          <ExternalLink className="w-3 h-3" />
          {t('workflow.social.postPicker.openPost', { network, defaultValue: `Ver no ${network}` })}
        </a>
      )}

      {/* Lista de publicações */}
      {open && listAvailable && (
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {username && (
            <p className="px-3 py-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
              {t('workflow.social.postPicker.fromAccount', { username, defaultValue: `Publicações de @${username}` })}
            </p>
          )}
          <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            <li>
              <button
                type="button"
                onClick={() => choose('')}
                className={`w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!value ? accent.row : ''}`}
              >
                <div className="w-12 h-12 shrink-0 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-gray-400" />
                </div>
                <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.postPicker.allPosts', 'Todas as publicações')}</span>
                {!value && <Check className={`w-4 h-4 ${accent.check} shrink-0`} />}
              </button>
            </li>
            {posts.map((post) => {
              const selected = post.id === value;
              const meta = [formatDate(post.timestamp), typeLabel(post.media_type)].filter(Boolean).join(' · ');
              return (
                <li key={post.id}>
                  <button
                    type="button"
                    onClick={() => choose(post.id)}
                    className={`w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selected ? accent.row : ''}`}
                  >
                    <PostThumb post={post} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2 break-words">
                        {post.caption || <span className="text-gray-400">{t('workflow.social.postPicker.noCaption', '(sem legenda)')}</span>}
                      </p>
                      {meta && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{meta}</p>}
                    </div>
                    {selected && <Check className={`w-4 h-4 ${accent.check} shrink-0`} />}
                  </button>
                </li>
              );
            })}
            {posts.length === 0 && (
              <li className="p-3 text-sm text-gray-500 dark:text-gray-400">
                {t('workflow.social.postPicker.empty', 'Nenhuma publicação encontrada nessa conta.')}
              </li>
            )}
          </ul>
          {nextCursor && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary-600 border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-60"
            >
              {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('workflow.social.postPicker.loadMore', 'Carregar mais')}
            </button>
          )}
        </div>
      )}

      {/* Sem conta / erro: avisa e deixa o campo manual à mostra */}
      {!loading && errorCode && (
        <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-800 dark:text-amber-200">{errorMessage()}</p>
            {errorCode !== 'not_connected' && (
              <button
                type="button"
                onClick={() => load(true)}
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-200 hover:underline"
              >
                <RefreshCw className="w-3 h-3" />
                {t('workflow.social.postPicker.retry', 'Tentar de novo')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Alternativa: colar o ID */}
      {!loading && !errorCode && (
        <button
          type="button"
          onClick={() => setManual((m) => !m)}
          className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline underline-offset-2"
        >
          {manual
            ? t('workflow.social.postPicker.hideManual', 'Esconder campo de ID')
            : t('workflow.social.postPicker.pasteId', 'Colar ID da publicação')}
        </button>
      )}
      {showManual && (
        <div className="mt-2">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value.trim())}
            placeholder={t('workflow.social.postIdPlaceholder', 'Post ID or leave empty for all')}
          />
        </div>
      )}
    </div>
  );
};

export default SocialPostPicker;
